import { useEffect, useRef } from "react";
import {
  ARKIT52_MORPHS,
  type ARKit52Morph,
  type ElevenLabsAlignment,
  getActiveVisemeAt,
  NATIVE_VISEME_MORPHS,
  type NativeVisemeMorph,
  getActiveNativeVisemeAt,
} from "@/lib/visemeMapper";
import {
  smoothVisemeAsymmetric,
  ATTACK_TAU_MS,
  RELEASE_TAU_MS,
  DT_CLAMP_MAX_MS,
  SILENCE_THRESHOLD,
} from "@/lib/audioLipSync";
import { getSharedAudioContext } from "@/lib/iosAudioUnlock";

// ─────────────────────────────────────────────────────────────────────────────
// LIP-SYNC — viseme-first, amplitude fallback.
//
// PRIMARY (alignment present): the ElevenLabs character-level `alignment` that
// already reaches the client drives per-PHONEME mouth shapes via the existing
// visemeMapper.VISEME_MAP (jaw + funnel + pucker + close + smile…), sampled at
// audioElement.currentTime. Real lip-sync — shapes, not a loudness flap.
//
// FALLBACK (no/empty alignment): an OFFLINE-decoded RMS amplitude envelope
// drives the mouth — jawOpen only on the ARKit channel; on the NATIVE channel
// (MetaPerson) it drives the open-vowel visemes (`aa` carrier + E/oh near the
// top; jawOpen is NOT a native viseme and would be a silent no-op). Fase 2 da
// voz: o Free fala pelo OpenAI tts-1 SEM alignment, então sem esta perna a
// boca de 94% do tráfego ficaria parada. RECALIBRATED soft (low cap) so it
// never regresses to the old "open wide / snap shut" flap. decodeAudioData
// never opens the output device (no audio routing, no ducking — preserves the
// #184 native-media path).
//
// Both paths share the asymmetric attack/release smoothing. Photoreal faces
// read 0.55–0.6 jawOpen as "shouting", so jawOpen is hard-capped low.
//
// FROZEN-SAFE: this hook is the ONLY editable surface. The morph mesh comes
// from AvatarCore (frozen), and every write is guarded by morph existence
// (`if (idx === undefined) continue`) so a model that lacks a morph is a no-op.
// ─────────────────────────────────────────────────────────────────────────────

// Mesh-shaped subset of THREE.Mesh — kept minimal so this hook doesn't
// pull a Three.js dependency. Caller (AvatarCore) passes the morph mesh
// directly.
export interface LipSyncMorphTarget {
  morphTargetDictionary: Record<string, number>;
  morphTargetInfluences: number[];
  /** Optional mesh name for diagnostic logs. */
  name?: string;
}

export interface UseLipSyncOptions {
  audioElement: HTMLAudioElement | null;
  // PRIMARY driver when present + non-empty (ElevenLabs /with-timestamps).
  alignment?: ElevenLabsAlignment | null;
  morphTarget: LipSyncMorphTarget | null;
  smoothingAlpha?: number;
  enabled?: boolean;
}

// ── Tuning constants (named, safe to adjust by eye) ───────────────────────────

// Viseme (alignment-driven) path.
const VISEME_INTENSITY = 0.8; // global softener — VISEME_MAP weights run hot on a photoreal face
const MAX_JAW_OPEN = 0.4; // hard cap on jawOpen; 0.55–0.6 reads as shouting

// Output-latency phase compensation (viseme path ONLY). The viseme shapes are
// sampled at audioElement.currentTime — the DECODE playhead — but the sound is
// only audible ~ctx.outputLatency later (large output buffers on the native
// WKWebView), so the mouth LEADS the audio. We subtract a phase offset from the
// sampled time. This default is the conservative WKWebView estimate, used when
// the platform reports no trustworthy ctx.outputLatency (it commonly reports
// 0/NaN). The dev HUD (window.__VYV_LIPSYNC.phaseMs) overrides it live so it can
// be dialed on-device. NOT YET calibrated — pending Rafa's device-test.
const LIPSYNC_PHASE_OFFSET_MS = 160;

// Amplitude fallback path (only when alignment is missing/empty).
const FALLBACK_ENERGY_SCALE = 5; // RMS→energy gain (was 6)
const FALLBACK_JAW_GAIN = 1.1; // softer than the old 1.5
const FALLBACK_JAW_CURVE = 0.7; // <1 → opens fast at low energy, eases near the top
const FALLBACK_MAX_JAW = 0.38; // soft cap (was 0.6 — the flap)

// Envelope sampling resolution for the fallback path.
const HOP_MS = 16;

// ── Dev calibration bridge (window.__VYV_LIPSYNC) ─────────────────────────────
// A single global the dev-only LipSyncCalibrationHUD reads/writes so phase, jaw
// cap, intensity and the smoothing τ can be dialed LIVE on device without a
// rebuild. In production the HUD never mounts: the object stays empty, every
// lookup falls back to the constants above, and this costs a few property reads
// per frame. SSR/test-safe (guards `window`).
export interface VyvLipSyncDebug {
  /** Phase offset override (ms). When a finite number, wins over the resolved latency. */
  phaseMs?: number;
  /** Release smoothing τ override (ms) — the lag knob. */
  releaseTauMs?: number;
  /** Attack smoothing τ override (ms). */
  attackTauMs?: number;
  /** jawOpen hard-cap override. */
  maxJaw?: number;
  /** Global viseme weight softener override. */
  intensity?: number;
  /** Telemetry written by the hook: device-reported output latency (ms) or null. */
  measuredOutputLatencyMs?: number | null;
  /** Telemetry written by the hook: the phase offset actually applied (ms). */
  effectivePhaseMs?: number;
}

declare global {
  interface Window {
    __VYV_LIPSYNC?: VyvLipSyncDebug;
  }
}

// Lazily-initialized accessor for the shared debug object. Returns a throwaway
// object off-window (SSR/tests) so callers never branch on null.
function getLipSyncDebug(): VyvLipSyncDebug {
  if (typeof window === "undefined") return {};
  return (window.__VYV_LIPSYNC ??= {});
}

// Returns `v` when it is a finite number, else `fallback`. Lets HUD overrides
// (unknown at runtime) coexist with typed defaults.
function numOr(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

// Reads the shared context's output latency in ms, or null when the platform
// reports nothing trustworthy (WKWebView commonly returns 0/NaN/undefined).
// Prefers outputLatency, then baseLatency. Never creates audio output.
function measureOutputLatencyMs(): number | null {
  const ctx = getSharedAudioContext();
  if (!ctx) return null;
  const out = (ctx as AudioContext & { outputLatency?: number }).outputLatency;
  if (typeof out === "number" && Number.isFinite(out) && out > 0) return out * 1000;
  const base = ctx.baseLatency;
  if (typeof base === "number" && Number.isFinite(base) && base > 0) return base * 1000;
  return null;
}

// Decoded RMS envelopes keyed by audio src (blob URL). Module-level so a clip
// replayed across mounts (cached blob URL) decodes only once. Used by the
// fallback path only.
const envelopeCache = new Map<string, Float32Array>();

// Per-hop RMS of channel 0. Pure, synchronous, never throws.
function computeEnvelope(buffer: AudioBuffer): Float32Array {
  const ch = buffer.getChannelData(0);
  const hop = Math.max(1, Math.round(buffer.sampleRate * (HOP_MS / 1000)));
  const n = Math.max(1, Math.ceil(ch.length / hop));
  const rms = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const start = i * hop;
    const end = Math.min(ch.length, start + hop);
    let sumSq = 0;
    for (let j = start; j < end; j++) sumSq += ch[j] * ch[j];
    rms[i] = Math.sqrt(sumSq / Math.max(1, end - start));
  }
  return rms;
}

// Fetch + offline-decode the clip at `src` into an RMS envelope. Uses the shared
// AudioContext only for decodeAudioData (no destination connection → no output,
// no ducking). Returns null on any failure (lip-sync degrades to a closed mouth).
async function ensureEnvelope(src: string): Promise<Float32Array | null> {
  if (!src) return null;
  const cached = envelopeCache.get(src);
  if (cached) return cached;
  try {
    const ctx = getSharedAudioContext();
    if (!ctx) return null;
    const res = await fetch(src);
    const arr = await res.arrayBuffer();
    const buf = await ctx.decodeAudioData(arr);
    const env = computeEnvelope(buf);
    envelopeCache.set(src, env);
    return env;
  } catch (err) {
    console.warn("[useLipSync] envelope decode failed; lip-sync disabled for clip", err);
    return null;
  }
}

// Soften + cap the raw per-phoneme viseme weights for a photoreal face.
// `intensity` and `maxJaw` are passed in (not closed over the module constants)
// so the dev HUD can adjust them live; production passes the constants.
function scaleVisemeTarget(
  raw: Partial<Record<ARKit52Morph, number>>,
  intensity: number,
  maxJaw: number,
): Partial<Record<ARKit52Morph, number>> {
  const out: Partial<Record<ARKit52Morph, number>> = {};
  for (const key in raw) {
    const morph = key as ARKit52Morph;
    let v = (raw[morph] ?? 0) * intensity;
    if (morph === "jawOpen") v = Math.min(v, maxJaw);
    out[morph] = v;
  }
  return out;
}

// Amplitude-only mouth weights (fallback): jaw opens with loudness, soft-capped.
function amplitudeFallbackTarget(energy: number): Partial<Record<ARKit52Morph, number>> {
  if (energy < SILENCE_THRESHOLD) return {};
  const jaw = Math.min(
    FALLBACK_MAX_JAW,
    Math.pow(energy, FALLBACK_JAW_CURVE) * FALLBACK_JAW_GAIN,
  );
  return { jawOpen: jaw };
}

// ── NATIVE amplitude fallback (Fase 2 da voz) ─────────────────────────────────
// O tráfego roteado pro OpenAI tts-1 chega SEM alignment, e no canal native o
// fallback jawOpen-only seria no-op silencioso (jawOpen não está em
// NATIVE_VISEME_MORPHS) — a boca do MetaPerson ficava parada. Aqui a energia
// RMS vira visemas de VOGAL ABERTA: `aa` como portador principal (mesma curva
// perceptual do fallback ARKit — reuso de FALLBACK_JAW_CURVE/GAIN), com um
// pouco de `E` e `oh` misturados perto do topo para a boca não parecer um
// "aa" congelado nos picos. Tetos calibrados ABAIXO do caminho por alignment
// (aa chega a 0.85 raw lá; aqui para em 0.70 antes do VISEME_INTENSITY), para
// o fallback nunca abrir mais que a fala com visemas reais.
const NATIVE_FALLBACK_AA_MAX = 0.7; // portador; alignment path chega a 0.85
const NATIVE_FALLBACK_TOP_KNEE = 0.6; // acima desta energia, E/oh entram
const NATIVE_FALLBACK_E_MAX = 0.18;
const NATIVE_FALLBACK_OH_MAX = 0.14;

// Exportada para o teste (src/test/lipSyncNativeFallback.test.ts) exercitar a
// curva sem montar o hook. NUNCA emite jawOpen — pinado lá.
export function amplitudeNativeFallbackTarget(
  energy: number,
): Partial<Record<NativeVisemeMorph, number>> {
  if (energy < SILENCE_THRESHOLD) return {};
  const drive = Math.min(
    1,
    Math.pow(energy, FALLBACK_JAW_CURVE) * FALLBACK_JAW_GAIN,
  );
  const target: Partial<Record<NativeVisemeMorph, number>> = {
    aa: drive * NATIVE_FALLBACK_AA_MAX,
  };
  const top = (energy - NATIVE_FALLBACK_TOP_KNEE) / (1 - NATIVE_FALLBACK_TOP_KNEE);
  if (top > 0) {
    target.E = top * NATIVE_FALLBACK_E_MAX;
    target.oh = top * NATIVE_FALLBACK_OH_MAX;
  }
  return target;
}

// ── Viseme channel feature-detection ──────────────────────────────────────────
// Two rigs reach this hook. Avaturn/RPM export the ARKit mouth names
// (mouthClose/Funnel/Pucker…). MetaPerson's default export exposes 15 native
// visemes (PP/FF/…/ou) and, apart from `jawOpen`, NONE of the ARKit mouth
// names — so ARKit writes are silent no-ops there. We inspect the loaded mesh's
// morphTargetDictionary and drive the channel it actually has. NEVER both.
//
// Discriminator: presence of PP + FF + an O/U rounder (oh|ou). These are native-
// only names an ARKit rig never carries, so a false positive is not possible;
// anything else falls through to the ARKit path (unchanged behaviour).
type LipChannel = "native" | "arkit";

function detectChannel(dict: Record<string, number>): LipChannel {
  const has = (n: string) => dict[n] !== undefined;
  return has("PP") && has("FF") && (has("oh") || has("ou")) ? "native" : "arkit";
}

// Soften the raw native-viseme weights by `intensity` (same knob the ARKit path
// uses). No jawOpen cap here: the native visemes are self-contained poses that
// already encode their own jaw drop, and jawOpen is NOT in NATIVE_VISEME_MORPHS.
function scaleNativeTarget(
  raw: Partial<Record<NativeVisemeMorph, number>>,
  intensity: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const key in raw) {
    out[key] = (raw[key as NativeVisemeMorph] ?? 0) * intensity;
  }
  return out;
}

// Native twin of smoothVisemeAsymmetric (audioLipSync.ts): same frame-rate-
// independent asymmetric attack/release, but iterating NATIVE_VISEME_MORPHS.
// A local copy is required because the shared smoother loops the fixed
// ARKIT52_MORPHS and audioLipSync.ts is out of this change's scope.
function smoothNativeAsymmetric(
  current: Record<string, number>,
  target: Record<string, number>,
  dtMs: number,
  attackTauMs: number,
  releaseTauMs: number,
): Record<string, number> {
  const attackAlpha = 1 - Math.exp(-dtMs / attackTauMs);
  const releaseAlpha = 1 - Math.exp(-dtMs / releaseTauMs);
  const result: Record<string, number> = {};
  for (const morph of NATIVE_VISEME_MORPHS) {
    const cur = current[morph] ?? 0;
    const tgt = target[morph] ?? 0;
    const alpha = tgt > cur ? attackAlpha : releaseAlpha;
    result[morph] = cur + (tgt - cur) * alpha;
  }
  return result;
}

// Drives the GLB's mouth morphs each animation frame — viseme-first, amplitude
// fallback. Self-contained: pause/end reset to neutral; missing alignment AND
// missing envelope both yield a closed mouth and never throw.
export function useLipSync({
  audioElement,
  alignment,
  morphTarget,
  // `smoothingAlpha` stays in UseLipSyncOptions for call-site compat but is
  // unused — per-frame smoothing uses ATTACK_TAU_MS / RELEASE_TAU_MS.
  enabled = true,
}: UseLipSyncOptions): void {
  const rafRef = useRef<number | null>(null);
  // Holds the last smoothed weights. Widened to Record<string, number> so it can
  // carry EITHER ARKit names or native viseme names, depending on the detected
  // channel; only one channel's keys are ever present at a time.
  const currentWeightsRef = useRef<Record<string, number>>({});
  const lastFrameTimeRef = useRef<number | null>(null);
  const envelopeRef = useRef<Float32Array | null>(null);
  const decodedSrcRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !audioElement || !morphTarget) {
      currentWeightsRef.current = {};
      return;
    }
    let disposed = false;

    // Viseme path is preferred whenever real alignment is present. The amplitude
    // envelope is only decoded/used when alignment is missing or empty.
    const hasAlignment =
      !!alignment &&
      Array.isArray(alignment.characters) &&
      alignment.characters.length > 0;

    // Resolve the output-latency phase offset ONCE per clip (viseme path only):
    // the device-measured latency when trustworthy, else the conservative
    // constant. The HUD can still override per-frame via window.__VYV_LIPSYNC.
    // Measured value is mirrored to the dev bridge as read-only telemetry.
    const measuredOutputLatencyMs = measureOutputLatencyMs();
    const resolvedPhaseMs = measuredOutputLatencyMs ?? LIPSYNC_PHASE_OFFSET_MS;
    getLipSyncDebug().measuredOutputLatencyMs = measuredOutputLatencyMs;

    // Decide the mouth channel ONCE per mounted mesh (the effect re-runs when
    // `morphTarget` changes, so this caches per avatar). Everything below drives
    // exactly this channel's names — never both.
    const dict = morphTarget.morphTargetDictionary;
    const channel = detectChannel(dict);
    const channelNames = channel === "native" ? NATIVE_VISEME_MORPHS : ARKIT52_MORPHS;

    // Self-diagnostic: count how many of the chosen channel's MOUTH-SHAPE names
    // actually exist in this mesh's dictionary (jawOpen excluded — it's the one
    // name shared by both rigs and alone it can only flap, not shape). When the
    // coverage is under the threshold, the per-frame writes would be silent
    // no-ops, so we surface the exact miss on the first load instead of shipping
    // a mute mouth. Reuses the same dict AvatarCore enumerates in its
    // [lipsync-debug] mesh log.
    const MOUTH_SHAPE_MIN = 3;
    let mouthShapeCoverage = 0;
    for (const morph of channelNames) {
      if (morph === "jawOpen") continue;
      if (dict[morph] !== undefined) mouthShapeCoverage++;
    }
    if (mouthShapeCoverage < MOUTH_SHAPE_MIN) {
      console.warn(
        "[lipsync-diag] low mouth-shape coverage — lip-sync will barely move." +
          " mesh=" + (morphTarget.name ?? "(unnamed)") +
          " channel=" + channel +
          " matched=" + mouthShapeCoverage + "/" + (channelNames.length - 1) +
          " (need >=" + MOUTH_SHAPE_MIN + "). The rig likely exports a different" +
          " blendshape set than the driven channel.",
        { channel, mouthShapeCoverage, dictKeys: Object.keys(dict) },
      );
    }

    const resetToNeutral = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      for (const morph of channelNames) {
        const idx = dict[morph];
        if (idx === undefined) continue;
        morphTarget.morphTargetInfluences[idx] = 0;
      }
      currentWeightsRef.current = {};
      lastFrameTimeRef.current = null;
    };

    // (Re)decode the RMS envelope for the FALLBACK path only. Skipped entirely
    // when alignment is present (viseme path needs no envelope).
    const syncEnvelope = () => {
      if (hasAlignment) return;
      const src = audioElement.currentSrc || audioElement.src || "";
      if (!src || src === decodedSrcRef.current) return;
      decodedSrcRef.current = src;
      envelopeRef.current = null;
      void ensureEnvelope(src).then((env) => {
        if (!disposed && decodedSrcRef.current === src && (audioElement.currentSrc || audioElement.src) === src) {
          envelopeRef.current = env;
          // A transient decode failure may be retried on the next play.
          if (!env) decodedSrcRef.current = null;
        }
      });
    };

    const tick = (frameTime: number) => {
      const last = lastFrameTimeRef.current;
      const dtMs = last === null ? 16.7 : Math.min(DT_CLAMP_MAX_MS, frameTime - last);
      lastFrameTimeRef.current = frameTime;

      // Read live overrides once per frame (no-op object in production).
      const dbg = getLipSyncDebug();

      // ── NATIVE channel (MetaPerson) ─────────────────────────────────────────
      // Drives the 15 native visemes and RETURNS, so the ARKit block below is
      // reached only by ARKit rigs and stays byte-identical (zero Avaturn risk).
      // Two drivers, same precedence as ARKit: alignment quando existe (visemas
      // por fonema); sem alignment (Fase 2: Free no OpenAI tts-1, que não manda
      // timestamps), o envelope RMS alimenta os visemas de vogal aberta via
      // amplitudeNativeFallbackTarget — `aa` portador, E/oh no topo, NUNCA
      // jawOpen (não está em NATIVE_VISEME_MORPHS; a escrita seria no-op).
      // Mesmo smoothNativeAsymmetric e mesmo VISEME_INTENSITY nos dois casos.
      if (channel === "native") {
        let nativeTarget: Record<string, number> = {};
        if (!audioElement.paused && !audioElement.ended) {
          if (hasAlignment) {
            const phaseMs = numOr(dbg.phaseMs, resolvedPhaseMs);
            dbg.effectivePhaseMs = phaseMs;
            const t = Math.max(0, audioElement.currentTime - phaseMs / 1000);
            nativeTarget = scaleNativeTarget(
              getActiveNativeVisemeAt(t, alignment!),
              numOr(dbg.intensity, VISEME_INTENSITY),
            );
          } else {
            // FALLBACK por amplitude (sem phase offset, como no ARKit): o
            // envelope já foi decodado pelo syncEnvelope — ele é agnóstico de
            // canal e só era ignorado aqui.
            const env = envelopeRef.current;
            if (env) {
              const idx = Math.floor((audioElement.currentTime * 1000) / HOP_MS);
              const rms = idx >= 0 && idx < env.length ? env[idx] : 0;
              const energy = Math.min(1, rms * FALLBACK_ENERGY_SCALE);
              nativeTarget = scaleNativeTarget(
                amplitudeNativeFallbackTarget(energy),
                numOr(dbg.intensity, VISEME_INTENSITY),
              );
            }
          }
        }
        const smoothedNative = smoothNativeAsymmetric(
          currentWeightsRef.current,
          nativeTarget,
          dtMs,
          numOr(dbg.attackTauMs, ATTACK_TAU_MS),
          numOr(dbg.releaseTauMs, RELEASE_TAU_MS),
        );
        currentWeightsRef.current = smoothedNative;
        for (const morph of NATIVE_VISEME_MORPHS) {
          const idx = dict[morph];
          if (idx === undefined) continue;
          morphTarget.morphTargetInfluences[idx] = Math.min(1, Math.max(0, smoothedNative[morph] ?? 0));
        }
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      let target: Partial<Record<ARKit52Morph, number>> = {};
      if (!audioElement.paused && !audioElement.ended) {
        if (hasAlignment) {
          // PRIMARY: per-phoneme shapes at the audible playhead, phase-compensated.
          // currentTime is the decode playhead; the sound is audible ~outputLatency
          // later, so we sample slightly in the PAST to stop the mouth leading.
          const phaseMs = numOr(dbg.phaseMs, resolvedPhaseMs);
          dbg.effectivePhaseMs = phaseMs;
          const t = Math.max(0, audioElement.currentTime - phaseMs / 1000);
          target = scaleVisemeTarget(
            getActiveVisemeAt(t, alignment!),
            numOr(dbg.intensity, VISEME_INTENSITY),
            numOr(dbg.maxJaw, MAX_JAW_OPEN),
          );
        } else {
          // FALLBACK: recalibrated amplitude → jawOpen only. (No phase offset.)
          const env = envelopeRef.current;
          if (env) {
            const idx = Math.floor((audioElement.currentTime * 1000) / HOP_MS);
            const rms = idx >= 0 && idx < env.length ? env[idx] : 0;
            const energy = Math.min(1, rms * FALLBACK_ENERGY_SCALE);
            target = amplitudeFallbackTarget(energy);
          }
        }
      }

      const smoothed = smoothVisemeAsymmetric(
        currentWeightsRef.current,
        target,
        dtMs,
        numOr(dbg.attackTauMs, ATTACK_TAU_MS),
        numOr(dbg.releaseTauMs, RELEASE_TAU_MS),
      );
      currentWeightsRef.current = smoothed;

      for (const morph of ARKIT52_MORPHS) {
        const idx = morphTarget.morphTargetDictionary[morph];
        if (idx === undefined) continue;
        morphTarget.morphTargetInfluences[idx] = Math.min(1, Math.max(0, smoothed[morph]));
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    const startPlayback = () => {
      syncEnvelope();
      // pause/ended cancel the RAF. Restart it for replay and sentence chains
      // even when React reuses the same audio element and null alignment.
      if (rafRef.current === null && !audioElement.paused && !audioElement.ended) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    startPlayback();

    audioElement.addEventListener("play", startPlayback);
    audioElement.addEventListener("loadeddata", syncEnvelope);
    audioElement.addEventListener("pause", resetToNeutral);
    audioElement.addEventListener("ended", resetToNeutral);

    return () => {
      disposed = true;
      decodedSrcRef.current = null;
      envelopeRef.current = null;
      audioElement.removeEventListener("play", startPlayback);
      audioElement.removeEventListener("loadeddata", syncEnvelope);
      audioElement.removeEventListener("pause", resetToNeutral);
      audioElement.removeEventListener("ended", resetToNeutral);
      resetToNeutral();
    };
  }, [audioElement, alignment, morphTarget, enabled]);
}
